import type { HeaderPair } from "../../shared/requestLogRecord";
import { base64ToUint8Array } from "./base64";

export type { HeaderPair };

export type DecodedRequestBody =
  | { kind: "empty" }
  | { kind: "text"; text: string }
  | { kind: "binary"; byteLength: number };

function findHeader(
  headers: readonly HeaderPair[],
  name: string
): string | undefined {
  const lower = name.toLowerCase();
  return headers.find(([key]) => key.toLowerCase() === lower)?.[1];
}

function isJsonContentType(contentType: string | undefined): boolean {
  const base = contentType?.split(";")[0]?.trim().toLowerCase();
  return base === "application/json" || (base?.endsWith("+json") ?? false);
}

// Bodies that aren't valid UTF-8 (images, protobuf, etc.) render as a byte
// count instead of mangled text.
export function decodeBodyBytes(
  bytes: Uint8Array,
  headers: readonly HeaderPair[] = []
): DecodedRequestBody {
  if (bytes.length === 0) return { kind: "empty" };

  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return { kind: "binary", byteLength: bytes.length };
  }

  if (isJsonContentType(findHeader(headers, "content-type"))) {
    try {
      return { kind: "text", text: JSON.stringify(JSON.parse(text), null, 2) };
    } catch {
      // Not actually valid JSON despite the content-type; fall through to raw text.
    }
  }
  return { kind: "text", text };
}

export function decodeRequestBody(
  base64: string | undefined,
  headers: readonly HeaderPair[] = []
): DecodedRequestBody {
  if (!base64) return { kind: "empty" };
  return decodeBodyBytes(base64ToUint8Array(base64), headers);
}
